/**
 * useDocumentTitle — route-aware browser tab title.
 *
 * Without this hook every SPA route shows the static index.html title,
 * so a user with five tabs open can't tell which is "Settings" vs
 * "Messages" vs "Admin dashboard". On navigation away we restore the
 * original title so no other consumer leaks into another hook's tab
 * title.
 */

import { useEffect, useRef } from 'react';

const BRAND_SUFFIX = ' · Chiefy';

export function useDocumentTitle(title: string | null | undefined): void {
    const originalRef = useRef<string>(typeof document !== 'undefined' ? document.title : '');

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!title) {
            document.title = originalRef.current;
            return;
        }
        const next = title.endsWith(BRAND_SUFFIX) ? title : `${title}${BRAND_SUFFIX}`;
        document.title = next;
        const original = originalRef.current;
        return () => {
            // Restore on unmount so a short-lived modal/pane that set
            // the title doesn't permanently shadow the page title.
            document.title = original;
        };
    }, [title]);
}
